import { DateField, DateFieldProps } from "@cmsgov/ds-medicare-gov";
import { useEffect, useState } from "react";
import capitalizeFirstLetter from "../functions/capitalizeFirstLetter";
import translate from "../functions/translate";
import { DateObject } from "../objects/models/date.field.value";


type DateFields2Props = Pick<DateFieldProps,
    'label' |
    'hint' |
    'monthFieldRef' |
    'errorMessage'
> & {
    date: DateObject
    hideDayField?: boolean
    /**
     * Called when any date input is blurred and the focus does not land on one
     * of the other date inputs inside this component (i.e., when the whole
     * component loses focus)
    */
    onComponentBlur?: DateFieldProps['onComponentBlur']
    onChange: (newVal: DateObject) => void
}
export const DateFields2 = (props: DateFields2Props) => {

    const { hideDayField } = props;
    const [dayRef, setDayRef] = useState<HTMLInputElement>();

    // hide day field
    useEffect(() => {
        if (hideDayField && dayRef) {
            const parentDiv = dayRef.closest('div');
            const separatorEl = parentDiv?.previousSibling as HTMLElement;
            [parentDiv, separatorEl].forEach(el => {
                el?.setAttribute('style', 'display: none');
            });
        }
    }, [hideDayField, dayRef]);

    const onChange = (dateObject: DateObject) => {
        const stripNonDigits = (x: string) => x.replace(/\D/g, '');
        let { day, month, year } = dateObject;
        day = stripNonDigits(day);
        month = stripNonDigits(month);
        year = stripNonDigits(year);

        if (day) {
            const dayNum = parseInt(day, 10) || 0;
            if (isNaN(dayNum) || dayNum < 0 || dayNum > 31) {
                day = '01';
            }
        }
        if (month) {
            const monthNum = parseInt(month, 10) || 0;
            if (isNaN(monthNum) || monthNum < 0 || monthNum > 12) {
                month = '01';
            }
        }

        props.onChange({ day, month, year });
    }

    const onBlur = (fieldName: string) => {
        let { day, month, year } = props.date;
        if (/^[1-9]$/.test(day) && fieldName === 'day') {
            day = day.padStart(2, '0');
        }
        if (/^[1-9]$/.test(month) && fieldName === 'month') {
            month = month.padStart(2, '0');
        }
        props.onChange({ day, month, year });
    }

    const hasError = !!props.errorMessage;

    return (
        <DateField
            autoComplete
            dayFieldRef={setDayRef}
            hint={props.hint}
            label={props.label ? props.label : ''}
            dayValue={props.hideDayField ? '01' : props.date.day}
            dayLabel={capitalizeFirstLetter(translate('day'))}
            dayInvalid={hasError}
            monthValue={props.date.month}
            monthLabel={capitalizeFirstLetter(translate('month'))}
            monthInvalid={hasError}
            yearValue={props.date.year}
            yearLabel={capitalizeFirstLetter(translate('year'))}
            yearInvalid={hasError}
            onChange={(_, dateObject) => onChange(dateObject)}
            onBlur={event => onBlur(event.currentTarget.name)}
            onComponentBlur={props.onComponentBlur}
            monthFieldRef={props.monthFieldRef}
            errorMessage={props.errorMessage}
        />
    );
};